import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	Stack,
	Typography,
} from "@mui/material";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import type {
	BarrelLayoutRow,
	RollsetAssignmentStatus,
	RollsetView,
} from "../models/RollsCartridge";
import { BarrelLayoutGrid } from "./BarrelLayoutGrid";

interface RollsetDetailPanelProps {
	rollset: RollsetView | null;
	assignmentStatus: RollsetAssignmentStatus | null;
	assignmentStatusLabel: string | null;
	opStatusCode: string | null;
	opStatusName: string | null;
	barrelLayout: BarrelLayoutRow[];
	isLoadingLayout: boolean;
	isDetailLoading: boolean;
	onPrepareClick: () => void;
	onUndoPrepareClick: () => void;
	isMutating: boolean;
	writeDisabled: boolean;
}

const FieldCell = ({
	label,
	value,
	valueStyle,
}: {
	label: string;
	value: React.ReactNode;
	valueStyle?: React.CSSProperties;
}) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "baseline",
			gap: 1,
			py: 0.5,
			minHeight: 28,
		}}
	>
		<Typography
			sx={{
				fontSize: 13,
				color: "text.secondary",
				fontWeight: 500,
				minWidth: 92,
			}}
		>
			{label}:
		</Typography>
		<Typography sx={{ fontSize: 14, fontWeight: 600, ...valueStyle }}>
			{value ?? "—"}
		</Typography>
	</Box>
);

const EmptyState = () => (
	<Box
		sx={{
			height: "100%",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			color: "text.secondary",
			gap: 1,
			p: 3,
		}}
	>
		<SearchIcon sx={{ fontSize: 72, color: "#cbd5e1" }} />
		<Typography variant="h6" sx={{ color: "#64748b" }}>
			No rollset selected
		</Typography>
		<Typography
			variant="body2"
			sx={{ color: "#94a3b8", textAlign: "center", maxWidth: 320 }}
		>
			Pick a rollset or a cartridge above, or click a row on the right, to see
			the rollset details and barrel layout.
		</Typography>
	</Box>
);

const opStatusColor = (code: string | null): string => {
	switch (code) {
		case "PREPAREDFORLINE":
			return "#2563eb"; // blue
		case "AVAILABLE":
			return "#16a34a"; // green
		case "INUSE":
			return "#c2410c"; // orange
		case "ASSEMBLE":
		case "PENDINGCHECK":
			return "#a855f7"; // purple
		default:
			return "#1e293b"; // slate
	}
};

export const RollsetDetailPanel = ({
	rollset,
	assignmentStatus,
	assignmentStatusLabel,
	opStatusCode,
	opStatusName,
	barrelLayout,
	isLoadingLayout,
	isDetailLoading,
	onPrepareClick,
	onUndoPrepareClick,
	isMutating,
	writeDisabled,
}: RollsetDetailPanelProps) => {
	if (!rollset) {
		// A rollset picked from a filter has no grid row to fall back on, so
		// there is a moment with nothing to render but the request in flight.
		return isDetailLoading ? (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<CircularProgress />
			</Box>
		) : (
			<EmptyState />
		);
	}

	const timestamp = rollset.Timestamp
		? dxtToLocalServerTime(rollset.Timestamp, "yyyy-MM-dd HH:mm:ss")
		: null;

	// Operational status button logic (mirrors legacy rolls-cartridge.aspx):
	// - Prepare button: shown when op status is NOT in ['INUSE','PREPAREDFORLINE']
	// - Undo Prepare: shown when op status is in ['INUSE','PREPAREDFORLINE'],
	//   but only enabled on PREPAREDFORLINE (can't undo once the rollset is INUSE).
	const isPreparedForLine = opStatusCode === "PREPAREDFORLINE";
	const isInUse = opStatusCode === "INUSE";
	const showUndoButton = isPreparedForLine || isInUse;
	const showPrepareButton = !showUndoButton;
	// Disable prepare if rolls are not both assigned
	const canPrepare = assignmentStatus === "ASSIGNED";

	const topDiam = rollset.TopRollDiameter;
	const bottomDiam = rollset.BottomRollDiameter;

	const barrelHeading = rollset.BarrelName ?? "Barrel layout";

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{/* Header with title + action button */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
					px: 2,
					py: 1.25,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
					Rollset: {rollset.Name ?? `#${rollset.Id}`}
				</Typography>

				{showUndoButton && (
					<WriteLockTooltip locked={writeDisabled}>
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={onUndoPrepareClick}
							disabled={writeDisabled || isMutating || !isPreparedForLine}
							title={
								isInUse
									? "Rollset is in use — cannot undo preparation"
									: "Remove the rollset from the prepared-for-line queue"
							}
						>
							{isMutating ? "Working…" : "Undo Prepare"}
						</Button>
					</WriteLockTooltip>
				)}
				{showPrepareButton && (
					<WriteLockTooltip locked={writeDisabled}>
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={onPrepareClick}
							disabled={writeDisabled || isMutating || !canPrepare}
							title={
								!canPrepare
									? "Rollset must have both top and bottom rolls to be prepared"
									: "Open the Prepare rollset dialog"
							}
						>
							{isMutating ? "Working…" : "Prepare for line"}
						</Button>
					</WriteLockTooltip>
				)}
			</Box>

			{/* Scrollable body */}
			<Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
				{/* 2-column key/value layout (legacy parity) */}
				<Grid2 container spacing={0} columnSpacing={3}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell label="Stand" value={rollset.StandName} />
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell label="Cartridge" value={rollset.CartridgeName} />
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell label="Top Roll" value={rollset.TopRollName} />
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell label="Bottom Roll" value={rollset.BottomRollName} />
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell
							label="Top Diameter"
							value={topDiam != null ? topDiam.toFixed(3) : null}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell
							label="Bottom Diameter"
							value={bottomDiam != null ? bottomDiam.toFixed(3) : null}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell label="Status" value={assignmentStatusLabel} />
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<FieldCell
							label="Op. Status"
							value={
								isDetailLoading ? (
									<CircularProgress size={14} />
								) : (
									(opStatusName ?? "—")
								)
							}
							valueStyle={{
								color: opStatusColor(opStatusCode),
								fontWeight: 700,
							}}
						/>
					</Grid2>

					{timestamp ? (
						<Grid2 size={{ xs: 12 }}>
							<FieldCell label="Prepared at" value={timestamp} />
						</Grid2>
					) : null}
				</Grid2>

				<Divider sx={{ my: 2 }} />

				<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
					Barrel: {barrelHeading}
				</Typography>

				{isLoadingLayout ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
						<CircularProgress size={24} />
					</Box>
				) : barrelLayout.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No barrel layout data available.
					</Typography>
				) : (
					<BarrelLayoutGrid rows={barrelLayout} />
				)}
			</Box>
		</Box>
	);
};
